<template>
    <div class="order-statistics">
        <main class="content">
            <el-card class="filter-card" shadow="never">
                <div class="section-title">
                    <i class="el-icon-search"></i> 筛选条件
                </div>
                <el-form :inline="true" :model="searchForm" size="mini" label-width="88px">
                    <el-form-item label="下单日期" required>
                        <el-date-picker
                            v-model="searchForm.dateRange"
                            type="daterange"
                            range-separator="至"
                            start-placeholder="开始日期"
                            end-placeholder="结束日期"
                            value-format="yyyy-MM-dd"
                            :picker-options="pickerOptions"
                            :clearable="false"
                            style="width: 240px">
                        </el-date-picker>
                    </el-form-item>
                    <el-form-item label="商品分类">
                        <el-cascader
                            v-model="searchForm.filterCategoryIds"
                            :props="categoryProps"
                            placeholder="全部分类"
                            clearable
                            filterable
                            style="width: 220px"
                            @change="onFilterCategoryChange">
                        </el-cascader>
                    </el-form-item>
                    <el-form-item label="客户">
                        <el-select
                            v-model="searchForm.companyIds"
                            multiple
                            filterable
                            collapse-tags
                            clearable
                            placeholder="全部客户"
                            style="width: 260px">
                            <el-option
                                v-for="item in companyOptions"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id">
                            </el-option>
                        </el-select>
                    </el-form-item>
                </el-form>

                <div class="section-title" style="margin-top: 8px;">
                    <i class="el-icon-s-operation"></i> 分组维度
                </div>
                <el-form :inline="true" :model="groupForm" size="mini" label-width="88px">
                    <el-form-item label="时间维度" required>
                        <el-radio-group v-model="groupForm.timeDimension">
                            <el-radio-button label="day">日</el-radio-button>
                            <el-radio-button label="month">月</el-radio-button>
                            <el-radio-button label="quarter">季度</el-radio-button>
                            <el-radio-button label="year">年</el-radio-button>
                        </el-radio-group>
                    </el-form-item>
                    <el-form-item label="">
                        <el-checkbox v-model="groupForm.groupByCategory">按商品分类</el-checkbox>
                        <el-select
                            v-model="groupForm.categoryGroupLevel"
                            :disabled="!groupForm.groupByCategory"
                            placeholder="分类层级"
                            size="mini"
                            style="width: 100px; margin-left: 8px;">
                            <el-option label="一级" :value="1"></el-option>
                            <el-option label="二级" :value="2"></el-option>
                            <el-option label="三级" :value="3"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="">
                        <el-checkbox v-model="groupForm.groupByCustomer">按客户</el-checkbox>
                    </el-form-item>
                    <el-form-item>
                        <el-button type="primary" icon="el-icon-search" :loading="loading" @click="doSearch">查询</el-button>
                        <el-button icon="el-icon-refresh-left" @click="resetSearch">重置</el-button>
                    </el-form-item>
                </el-form>
            </el-card>

            <el-row :gutter="16" class="summary-row">
                <el-col :span="12">
                    <el-card shadow="hover" class="summary-card summary-untaxed">
                        <div class="summary-label">销售金额（未税）</div>
                        <div class="summary-value">{{ formatMoney(summary.untaxedAmount) }}</div>
                        <div class="summary-unit">元 · 当前筛选全量合计</div>
                    </el-card>
                </el-col>
                <el-col :span="12">
                    <el-card shadow="hover" class="summary-card summary-taxed">
                        <div class="summary-label">销售金额（含税）</div>
                        <div class="summary-value">{{ formatMoney(summary.taxedAmount) }}</div>
                        <div class="summary-unit">元 · 当前筛选全量合计</div>
                    </el-card>
                </el-col>
            </el-row>

            <el-row :gutter="16" class="chart-table-row">
                <el-col :span="14">
                    <el-card shadow="hover" class="chart-card">
                        <div slot="header" class="card-header">
                            <span>销售金额趋势</span>
                            <el-radio-group v-model="chartMetric" size="mini" @change="renderChart">
                                <el-radio-button label="untaxed">未税</el-radio-button>
                                <el-radio-button label="taxed">含税</el-radio-button>
                            </el-radio-group>
                        </div>
                        <div v-loading="loading" ref="chartRef" class="chart-container"></div>
                        <div class="chart-footnote">图表基于当前筛选条件下全量统计，与顶部合计范围一致</div>
                    </el-card>
                </el-col>
                <el-col :span="10">
                    <el-card shadow="hover" class="chart-card">
                        <div slot="header" class="card-header">
                            <span>金额占比（含税 Top8）</span>
                        </div>
                        <div v-loading="loading" ref="pieChartRef" class="chart-container chart-container-sm"></div>
                    </el-card>
                </el-col>
            </el-row>

            <el-card shadow="hover" class="table-card">
                <div slot="header" class="card-header">
                    <span class="card-header-title">
                        <span>统计明细</span>
                        <span class="table-hint">{{ drillHintText }}</span>
                    </span>
                    <el-button
                        size="mini"
                        icon="el-icon-download"
                        :loading="exportLoading"
                        @click="exportStatistics">
                        下载
                    </el-button>
                </div>
                <div ref="tablePosition"></div>
                <el-table
                    :key="statisticsTableKey"
                    stripe
                    size="mini"
                    :data="tableData"
                    :row-key="getStatisticsRowKey"
                    v-loading="loading"
                    :height="tableConfig.height"
                    highlight-current-row
                    class="drill-table"
                    @row-click="handleRowDrill"
                    style="width: 100%">
                    <el-table-column
                        key="col-period"
                        prop="periodLabel"
                        :label="periodColumnLabel"
                        min-width="120"
                        fixed
                        show-overflow-tooltip>
                        <template slot-scope="scope">{{ scope.row.periodLabel || '-' }}</template>
                    </el-table-column>
                    <el-table-column
                        v-if="groupForm.groupByCategory"
                        key="col-category"
                        prop="categoryName"
                        label="商品分类"
                        min-width="160"
                        show-overflow-tooltip>
                        <template slot-scope="scope">{{ scope.row.categoryName || '-' }}</template>
                    </el-table-column>
                    <el-table-column
                        v-if="groupForm.groupByCustomer"
                        key="col-company"
                        prop="companyName"
                        label="客户"
                        min-width="160"
                        show-overflow-tooltip>
                        <template slot-scope="scope">{{ scope.row.companyName || '-' }}</template>
                    </el-table-column>
                    <el-table-column
                        key="col-untaxed"
                        prop="untaxedAmount"
                        label="销售金额（未税）"
                        min-width="140"
                        align="right">
                        <template slot-scope="scope">
                            {{ formatMoney(scope.row.untaxedAmount) }}
                        </template>
                    </el-table-column>
                    <el-table-column
                        key="col-taxed"
                        prop="taxedAmount"
                        label="销售金额（含税）"
                        min-width="140"
                        align="right">
                        <template slot-scope="scope">
                            {{ formatMoney(scope.row.taxedAmount) }}
                        </template>
                    </el-table-column>
                    <el-table-column key="col-action" label="操作" width="80" align="center" fixed="right">
                        <template slot-scope="scope">
                            <el-button type="text" size="mini" @click.stop="handleRowDrill(scope.row)">下钻</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <div class="pagination-wrap">
                    <el-pagination
                        background
                        layout="total, sizes, prev, pager, next"
                        :page-sizes="[10, 20, 50, 100]"
                        v-if="total > 0"
                        :total="total"
                        :page-size="pageSize"
                        :current-page="currentPage"
                        @size-change="handleSizeChange"
                        @current-change="onPageChange">
                    </el-pagination>
                </div>
            </el-card>
        </main>

        <el-dialog
            :title="drillDialogTitle"
            :visible.sync="drillVisible"
            :width="drillDialogWidth"
            top="8vh"
            :close-on-click-modal="false"
            append-to-body>
            <el-table
                v-if="drillGroupByCategory"
                stripe
                size="mini"
                :data="drillOrders"
                v-loading="drillLoading"
                max-height="480"
                style="width: 100%">
                <el-table-column prop="orderNo" label="订单编号" min-width="140" show-overflow-tooltip />
                <el-table-column prop="companyName" label="客户" min-width="120" show-overflow-tooltip />
                <el-table-column prop="orderTime" label="下单时间" width="155" />
                <el-table-column prop="name" label="商品名称" min-width="140" show-overflow-tooltip />
                <el-table-column prop="sku" label="SKU" width="110" show-overflow-tooltip />
                <el-table-column prop="num" label="数量" width="70" align="center" />
                <el-table-column prop="totalUntaxedPrice" label="未税金额(元)" width="110" align="right">
                    <template slot-scope="scope">{{ formatMoney(scope.row.totalUntaxedPrice) }}</template>
                </el-table-column>
                <el-table-column prop="totalPrice" label="未税金额(元)" width="110" align="right">
                    <template slot-scope="scope">{{ formatMoney(scope.row.totalPrice) }}</template>
                </el-table-column>
                <el-table-column prop="status" label="明细状态" width="90" align="center">
                    <template slot-scope="scope">
                        <el-tag size="mini" :type="detailStatusType(scope.row.status)">
                            {{ detailStatusText(scope.row.status) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="80" align="center" fixed="right">
                    <template slot-scope="scope">
                        <el-button type="text" size="mini" @click="goOrderDetail(scope.row)">订单</el-button>
                    </template>
                </el-table-column>
            </el-table>
            <el-table
                v-else
                stripe
                size="mini"
                :data="drillOrders"
                v-loading="drillLoading"
                max-height="480"
                style="width: 100%">
                <el-table-column prop="orderNo" label="订单编号" min-width="160" show-overflow-tooltip />
                <el-table-column prop="companyName" label="客户" min-width="140" show-overflow-tooltip />
                <el-table-column prop="orderTime" label="下单时间" width="160" />
                <el-table-column prop="totalUntaxedPrice" label="含税金额(元)" width="120" align="right">
                    <template slot-scope="scope">{{ formatMoney(scope.row.totalUntaxedPrice) }}</template>
                </el-table-column>
                <el-table-column prop="totalPrice" label="含税金额(元)" width="120" align="right">
                    <template slot-scope="scope">{{ formatMoney(scope.row.totalPrice) }}</template>
                </el-table-column>
                <el-table-column prop="status" label="订单状态" width="90" align="center">
                    <template slot-scope="scope">
                        <el-tag size="mini" :type="orderStatusType(scope.row.status)">
                            {{ orderStatusText(scope.row.status) }}
                        </el-tag>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="80" align="center" fixed="right">
                    <template slot-scope="scope">
                        <el-button type="text" size="mini" @click="goOrderDetail(scope.row)">详情</el-button>
                    </template>
                </el-table-column>
            </el-table>
            <div class="pagination-wrap">
                <el-pagination
                    background
                    layout="total, prev, pager, next"
                    v-if="drillTotal > 0"
                    :total="drillTotal"
                    :page-size="drillPageSize"
                    :current-page="drillCurrentPage"
                    @current-change="onDrillPageChange">
                </el-pagination>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import { downloadByPost, convertRes2Blob } from '../../utils/download.js';

const HEIGHT_OFFSET = 118;
const TIME_DIMENSION_LABELS = {
    day: '日期',
    month: '月份',
    quarter: '季度',
    year: '年度',
};
const DETAIL_STATUS_MAP = {
    ORDER: '已下单',
    DELIVERY: '已发货',
    RECEIVE: '已收货',
    2: '已下单',
    3: '已发货',
    4: '已收货',
};
const ORDER_STATUS_MAP = {
    ORDER: '已下单',
    DELIVERY: '已发货',
    DELIVERED: '已收货',
    0: '待下单',
    1: '预下单',
    2: '已下单',
    3: '已发货',
    4: '已收货',
    5: '已取消',
    6: '已驳回',
};

export default {
    name: 'OrderStatistics',
    data() {
        const that = this;
        const end = new Date();
        const start = new Date();
        start.setMonth(start.getMonth() - 3);
        const fmt = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };
        return {
            pickerOptions: {
                disabledDate(time) {
                    return time.getTime() > Date.now();
                },
            },
            searchForm: {
                dateRange: [fmt(start), fmt(end)],
                filterCategoryIds: [],
                filterCategoryId: null,
                filterCategoryLevel: null,
                companyIds: [],
            },
            groupForm: {
                timeDimension: 'month',
                groupByCategory: true,
                categoryGroupLevel: 2,
                groupByCustomer: true,
            },
            companyOptions: [],
            categoryLevelMap: {},
            categoryProps: {
                label: 'name',
                value: 'id',
                lazy: true,
                checkStrictly: true,
                lazyLoad(node, resolve) {
                    that.loadCategory(node, resolve);
                },
            },
            loading: false,
            exportLoading: false,
            tableData: [],
            total: 0,
            pageSize: 20,
            currentPage: 1,
            summary: {
                untaxedAmount: null,
                taxedAmount: null,
            },
            charts: {
                trend: [],
                pieTop: [],
            },
            tableConfig: { height: 360 },
            chartMetric: 'taxed',
            chartsBar: null,
            chartsPie: null,
            drillVisible: false,
            drillLoading: false,
            drillOrders: [],
            drillTotal: 0,
            drillPageSize: 10,
            drillCurrentPage: 1,
            drillRow: null,
            drillGroupByCategory: true,
        };
    },
    computed: {
        statisticsTableKey() {
            const g = this.groupForm;
            return [
                g.timeDimension,
                g.groupByCategory ? 1 : 0,
                g.groupByCustomer ? 1 : 0,
                g.categoryGroupLevel || 0,
            ].join('-');
        },
        periodColumnLabel() {
            return TIME_DIMENSION_LABELS[this.groupForm.timeDimension] || '时间';
        },
        drillHintText() {
            return this.groupForm.groupByCategory
                ? '点击行下钻查看订单明细'
                : '点击行下钻查看订单列表';
        },
        drillDialogWidth() {
            return this.drillGroupByCategory ? '1100px' : '960px';
        },
        drillDialogTitle() {
            if (!this.drillRow) {
                return this.drillGroupByCategory ? '订单明细' : '订单列表';
            }
            const parts = [];
            if (this.drillRow.periodLabel) {
                parts.push(this.drillRow.periodLabel);
            }
            if (this.drillGroupByCategory && this.drillRow.categoryName) {
                parts.push(this.drillRow.categoryName);
            }
            if (this.groupForm.groupByCustomer && this.drillRow.companyName) {
                parts.push(this.drillRow.companyName);
            }
            const prefix = this.drillGroupByCategory ? '订单明细' : '订单列表';
            return parts.length ? `${prefix} · ${parts.join(' / ')}` : prefix;
        },
    },
    methods: {
        getStatisticsRowKey(row) {
            const parts = [row.periodKey, row.categoryId, row.companyId]
                .filter((v) => v != null && v !== '');
            if (parts.length) {
                return parts.join('-');
            }
            return `${row.periodLabel || ''}-${row.untaxedAmount}-${row.taxedAmount}`;
        },
        loadCategory(node, resolve) {
            const parentLevel = node.root ? 0 : (node.data.level || 0);
            const params = { fid: node.root ? 'root' : node.value };
            this.$axios.post('/admin/product/category/list', params).then((response) => {
                const resp = response.data;
                if (resp.header.status === 0) {
                    const items = (resp.data || []).map((it) => {
                        const level = parentLevel + 1;
                        this.$set(this.categoryLevelMap, it.id, level);
                        return { ...it, level };
                    });
                    resolve(items);
                } else {
                    resolve([]);
                }
            }).catch(() => resolve([]));
        },
        onFilterCategoryChange(val) {
            if (!val || val.length === 0) {
                this.searchForm.filterCategoryId = null;
                this.searchForm.filterCategoryLevel = null;
                return;
            }
            const categoryId = val[val.length - 1];
            const level = this.categoryLevelMap[categoryId];
            if (level == null) {
                this.$message.warning('无法识别分类层级，请重新选择分类');
                this.searchForm.filterCategoryIds = [];
                this.searchForm.filterCategoryId = null;
                this.searchForm.filterCategoryLevel = null;
                return;
            }
            this.searchForm.filterCategoryId = categoryId;
            this.searchForm.filterCategoryLevel = level;
        },
        initCompanyOptions() {
            this.$axios.post('/admin/company/list', {}).then((response) => {
                const resp = response.data;
                if (resp.header.status === 0) {
                    this.companyOptions = resp.data || [];
                }
            });
        },
        validateSearch() {
            if (!this.searchForm.dateRange || this.searchForm.dateRange.length !== 2) {
                this.$message.warning('请选择下单日期范围');
                return false;
            }
            if (this.groupForm.groupByCategory && !this.groupForm.categoryGroupLevel) {
                this.$message.warning('开启按商品分类分组时，请选择分类层级');
                return false;
            }
            const { filterCategoryId, filterCategoryLevel } = this.searchForm;
            const hasFilterId = filterCategoryId != null && filterCategoryId !== '';
            const hasFilterLevel = filterCategoryLevel != null;
            if (hasFilterId !== hasFilterLevel) {
                this.$message.warning('商品分类筛选需同时选择分类节点');
                return false;
            }
            if (
                hasFilterLevel
                && this.groupForm.groupByCategory
                && filterCategoryLevel > this.groupForm.categoryGroupLevel
            ) {
                this.$message.warning('筛选分类不能比汇总层级更细，请调整筛选或汇总层级');
                return false;
            }
            return true;
        },
        buildCond(extra = {}) {
            const cond = {
                startDate: this.searchForm.dateRange[0],
                endDate: this.searchForm.dateRange[1],
                timeDimension: this.groupForm.timeDimension,
                groupByCategory: this.groupForm.groupByCategory,
                groupByCustomer: this.groupForm.groupByCustomer,
                filterCategoryId: null,
                filterCategoryLevel: null,
                companyIds: this.searchForm.companyIds && this.searchForm.companyIds.length
                    ? this.searchForm.companyIds
                    : [],
                ...extra,
            };
            if (this.groupForm.groupByCategory) {
                cond.categoryGroupLevel = this.groupForm.categoryGroupLevel;
            }
            if (this.searchForm.filterCategoryId) {
                cond.filterCategoryId = this.searchForm.filterCategoryId;
                cond.filterCategoryLevel = this.searchForm.filterCategoryLevel;
            }
            return cond;
        },
        parseSpringPage(payload) {
            if (!payload) {
                return { content: [], totalElements: 0 };
            }
            // 统计列表：{ page, summary }；下钻列表：data 即为 Page（content 在顶层）
            const page = payload.page || payload;
            return {
                content: page.content || [],
                totalElements: page.totalElements || 0,
            };
        },
        doSearch() {
            if (!this.validateSearch()) {
                return;
            }
            this.currentPage = 1;
            this.onSearch();
        },
        resetSearch() {
            const end = new Date();
            const start = new Date();
            start.setMonth(start.getMonth() - 3);
            const fmt = (d) => {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${y}-${m}-${day}`;
            };
            this.searchForm = {
                dateRange: [fmt(start), fmt(end)],
                filterCategoryIds: [],
                filterCategoryId: null,
                filterCategoryLevel: null,
                companyIds: [],
            };
            this.groupForm = {
                timeDimension: 'month',
                groupByCategory: true,
                categoryGroupLevel: 2,
                groupByCustomer: true,
            };
            this.doSearch();
        },
        onSearch() {
            if (!this.validateSearch()) {
                return;
            }
            this.loading = true;
            this.tableData = [];
            this.$axios.post('/admin/statistics/orderStatistics', {
                cond: this.buildCond(),
                page: this.currentPage - 1,
                size: this.pageSize,
            }).then((response) => {
                const resp = response.data;
                if (resp.header.status === 0) {
                    const data = resp.data || {};
                    const page = this.parseSpringPage(data);
                    this.tableData = page.content;
                    this.total = page.totalElements;
                    this.summary = data.summary || { untaxedAmount: null, taxedAmount: null };
                    const charts = data.charts || {};
                    this.charts = {
                        trend: charts.trend || [],
                        pieTop: charts.pieTop || [],
                    };
                    this.$nextTick(() => {
                        this.renderChart();
                    });
                } else {
                    this.$notify.error({ title: '错误', message: resp.header.msg });
                }
            }).catch(() => {}).finally(() => {
                this.loading = false;
            });
        },
        truncateText(str, maxLen = 14) {
            const s = str == null ? '' : String(str);
            if (s.length <= maxLen) {
                return s;
            }
            return `${s.slice(0, maxLen - 1)}…`;
        },
        buildPieSliceName(row) {
            const parts = [];
            if (row.periodLabel) {
                parts.push(row.periodLabel);
            }
            if (row.categoryName) {
                parts.push(row.categoryName);
            }
            if (row.companyName) {
                parts.push(row.companyName);
            }
            return parts.length ? parts.join('-') : '其他';
        },
        buildPieLegendName(row) {
            const full = this.buildPieSliceName(row);
            if (row.companyName) {
                const prefix = row.periodLabel ? `${row.periodLabel}·` : '';
                return this.truncateText(prefix + row.companyName, 16);
            }
            return this.truncateText(full, 16);
        },
        exportStatistics() {
            if (!this.validateSearch()) {
                return;
            }
            this.exportLoading = true;
            downloadByPost({
                axios: this.$axios,
                url: '/admin/statistics/orderStatistics/export',
                data: { cond: this.buildCond() },
                success: (response) => {
                    convertRes2Blob(response);
                },
                faild: () => {
                    this.$message.error('导出失败，请稍后重试');
                },
                complete: () => {
                    this.exportLoading = false;
                },
            });
        },
        renderChart() {
            const trend = this.charts.trend || [];
            const pieTop = this.charts.pieTop || [];
            const xData = trend.map((it) => it.periodLabel);
            const metricKey = this.chartMetric === 'untaxed' ? 'untaxedAmount' : 'taxedAmount';
            const seriesData = trend.map((it) => Number(it[metricKey]) || 0);
            const metricLabel = this.chartMetric === 'untaxed' ? '未税' : '含税';

            if (this.$refs.chartRef) {
                if (!this.chartsBar) {
                    this.chartsBar = echarts.init(this.$refs.chartRef);
                } else {
                    this.chartsBar.clear();
                }
                this.chartsBar.setOption({
                    color: ['#409EFF'],
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter: (v) => `${this.formatMoney(v)} 元`,
                    },
                    grid: { left: 48, right: 24, top: 40, bottom: 48 },
                    xAxis: {
                        type: 'category',
                        data: xData,
                        axisLabel: { rotate: xData.length > 8 ? 35 : 0 },
                    },
                    yAxis: {
                        type: 'value',
                        axisLabel: {
                            formatter: (v) => (v >= 10000 ? `${(v / 10000).toFixed(1)}万` : v),
                        },
                    },
                    series: [{
                        name: `销售金额（${metricLabel}）`,
                        type: 'bar',
                        barMaxWidth: 48,
                        data: seriesData,
                        itemStyle: {
                            borderRadius: [4, 4, 0, 0],
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                { offset: 0, color: '#66b1ff' },
                                { offset: 1, color: '#409EFF' },
                            ]),
                        },
                    }],
                });
            }

            if (this.$refs.pieChartRef) {
                const usedLegendNames = new Set();
                const pieData = pieTop.map((row, index) => {
                    const fullName = this.buildPieSliceName(row);
                    let legendName = this.buildPieLegendName(row);
                    while (usedLegendNames.has(legendName)) {
                        legendName = `${this.truncateText(fullName, 12)}(${index + 1})`;
                    }
                    usedLegendNames.add(legendName);
                    return {
                        name: legendName,
                        fullName,
                        value: Number(row.taxedAmount) || 0,
                    };
                });
                if (!this.chartsPie) {
                    this.chartsPie = echarts.init(this.$refs.pieChartRef);
                } else {
                    this.chartsPie.clear();
                }
                this.chartsPie.setOption({
                    tooltip: {
                        trigger: 'item',
                        formatter: (p) => {
                            const label = (p.data && p.data.fullName) || p.name;
                            return `${label}<br/>${this.formatMoney(p.value)} 元 (${p.percent}%)`;
                        },
                    },
                    legend: {
                        type: 'scroll',
                        orient: 'horizontal',
                        bottom: 0,
                        left: 'center',
                        width: '96%',
                        itemWidth: 10,
                        itemHeight: 10,
                        itemGap: 10,
                        textStyle: { fontSize: 11 },
                        tooltip: { show: true },
                    },
                    series: [{
                        type: 'pie',
                        radius: ['36%', '58%'],
                        center: ['50%', '42%'],
                        avoidLabelOverlap: true,
                        itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
                        label: { show: false },
                        data: pieData.length ? pieData : [{ name: '暂无数据', value: 0 }],
                    }],
                });
            }
        },
        handleSizeChange(pageSize) {
            this.pageSize = pageSize;
            this.onSearch();
        },
        onPageChange(page) {
            this.currentPage = page;
            this.onSearch();
        },
        handleRowDrill(row) {
            if (!row.periodKey) {
                this.$message.warning('缺少时间维度标识，无法下钻');
                return;
            }
            this.drillRow = row;
            this.drillGroupByCategory = this.groupForm.groupByCategory;
            this.drillCurrentPage = 1;
            this.drillVisible = true;
            this.loadDrillOrders();
        },
        buildDrillCond() {
            const row = this.drillRow || {};
            const drill = {
                drillPeriodKey: row.periodKey,
            };
            if (row.categoryId != null && row.categoryId !== '') {
                drill.drillCategoryId = row.categoryId;
            }
            if (row.companyId != null && row.companyId !== '') {
                drill.drillCompanyId = row.companyId;
            }
            return this.buildCond(drill);
        },
        loadDrillOrders() {
            this.drillLoading = true;
            this.$axios.post('/admin/statistics/orderStatistics/orders', {
                cond: this.buildDrillCond(),
                page: this.drillCurrentPage - 1,
                size: this.drillPageSize,
            }).then((response) => {
                const resp = response.data;
                if (resp.header.status === 0) {
                    const page = this.parseSpringPage(resp.data);
                    this.drillOrders = page.content;
                    this.drillTotal = page.totalElements;
                } else {
                    this.$notify.error({ title: '错误', message: resp.header.msg });
                }
            }).catch(() => {}).finally(() => {
                this.drillLoading = false;
            });
        },
        onDrillPageChange(page) {
            this.drillCurrentPage = page;
            this.loadDrillOrders();
        },
        goOrderDetail(row) {
            const orderId = this.drillGroupByCategory ? row.orderId : row.id;
            if (!orderId) {
                this.$message.warning('缺少订单 ID，无法跳转');
                return;
            }
            this.$router.push({
                name: 'customerReviewDetail',
                params: {
                    from: 'rfq',
                    orderId,
                    title: '订单详情',
                    step: row.reviewStep,
                    workflowId: row.workflowId,
                    name: row.consignee || row.companyName,
                    totalPrice: row.totalPrice,
                    showBtn: false,
                },
            });
        },
        formatMoney(value) {
            if (value == null || value === '') {
                return '-';
            }
            return this.MoneyFormat(value, '', 2);
        },
        detailStatusText(status) {
            if (status == null || status === '') return '-';
            return DETAIL_STATUS_MAP[status] || String(status);
        },
        detailStatusType(status) {
            if (status === 'RECEIVE' || status === 4) return 'success';
            if (status === 'DELIVERY' || status === 3) return 'warning';
            return 'info';
        },
        orderStatusText(status) {
            if (status == null || status === '') return '-';
            return ORDER_STATUS_MAP[status] != null ? ORDER_STATUS_MAP[status] : String(status);
        },
        orderStatusType(status) {
            if (status === 'DELIVERED' || status === 4) return 'success';
            if (status === 'DELIVERY' || status === 3) return 'warning';
            if (status === 5 || status === 6) return 'danger';
            return 'info';
        },
        setTableHeight() {
            const el = this.$refs.tablePosition;
            if (!el) return;
            const { top } = el.getBoundingClientRect();
            this.tableConfig.height = Math.max(280, window.innerHeight - top - HEIGHT_OFFSET);
        },
        onResize() {
            this.setTableHeight();
            if (this.chartsBar) this.chartsBar.resize();
            if (this.chartsPie) this.chartsPie.resize();
        },
    },
    created() {
        this.initCompanyOptions();
        this.onSearch();
        window.addEventListener('resize', this.onResize);
    },
    mounted() {
        this.$nextTick(() => {
            this.setTableHeight();
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
        if (this.chartsBar) {
            this.chartsBar.dispose();
            this.chartsBar = null;
        }
        if (this.chartsPie) {
            this.chartsPie.dispose();
            this.chartsPie = null;
        }
    },
};
</script>

<style scoped>
.order-statistics .filter-card {
    margin-bottom: 16px;
}
.section-title {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 12px;
}
.section-title i {
    color: #409EFF;
    margin-right: 4px;
}
.summary-row {
    margin-bottom: 16px;
}
.summary-card {
    text-align: center;
    border: none;
}
.summary-card >>> .el-card__body {
    padding: 20px 16px;
}
.summary-label {
    font-size: 13px;
    color: #909399;
    margin-bottom: 8px;
}
.summary-value {
    font-size: 26px;
    font-weight: 600;
    line-height: 1.2;
}
.summary-unit {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}
.summary-untaxed .summary-value { color: #409EFF; }
.summary-taxed .summary-value { color: #67C23A; }
.chart-table-row {
    margin-bottom: 16px;
}
.chart-card >>> .el-card__header {
    padding: 12px 16px;
}
.card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.chart-container {
    width: 100%;
    height: 300px;
}
.chart-container-sm {
    height: 300px;
}
.chart-footnote {
    font-size: 12px;
    color: #909399;
    text-align: center;
    padding: 0 16px 12px;
}
.table-card >>> .el-card__header {
    padding: 12px 16px;
}
.card-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}
.table-hint {
    font-size: 12px;
    color: #909399;
    font-weight: normal;
}
.drill-table >>> tbody tr {
    cursor: pointer;
}
.drill-table >>> tbody tr:hover > td {
    background-color: #ecf5ff !important;
}
.pagination-wrap {
    text-align: center;
    padding-top: 16px;
}
</style>
